import React, { useCallback, useEffect, useState, useMemo, useContext } from 'react';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import _ from 'underscore';
import { Overlay } from '@bingads-webui-react/fabric-control';
import { DisplayAdsEditor } from '@bingads-webui-campaign-react/display-ads-editor';
import { DataContext } from '@bingads-webui-campaign-react/ads-studio-shared-components';
import { AdsStudioSpinner, ClarityTags, useClarityTags, AdditionalQueries } from '@bingads-webui-campaign-react/ads-studio-app-shared-components';

import { useEditDisplayTemplatePageStyles } from './style';
import { useNotify } from '../create-display-template-page-component/use-notify';

const EditDisplayTemplatePage = ({
  logger,
  notificationApi,
  imageId,
  i18n,
  brandKitDataService,
  brandkitApiState,
  displayTemplatesDataService,
  dataService,
  permissions,
  aid,
  cid,
  oDataConfig,
  serverContext,
  preferenceService,
  pagePerfMarker: displayTemplateEditPagePerfMarker,
  setDataLoaded,
  navigation,
  search,
  instrumentation,
}) => {
  const classes = useEditDisplayTemplatePageStyles();
  const queries = queryString.parse(search);
  const {
    folderId = '',
    templateId = '',
  } = queries || {};

  const [templateDetails, setTemplateDetails] = useState(null);
  const [templateConfig, setTemplateConfig] = useState(null);
  const [displayAd, setDisplayAd] = useState(null);
  const [sourceType, setSourceType] = useState(null);
  const { data: imageDetails } = useContext(DataContext);
  const notify = useNotify({
    i18n, count: 1, navigation, logger,
  });

  const isPageLoading = useMemo(() => !templateDetails, [templateDetails]);

  const displayAdFromStorage = useMemo(() => {
    setSourceType(imageDetails ? imageDetails.sourceType : undefined);

    if (imageDetails && imageDetails.imageId === imageId &&
        imageDetails.metadata &&
        imageDetails.metadata.template &&
        imageDetails.metadata.template.templateId === templateId) {
      return {
        ...imageDetails.metadata,
        url: imageDetails.url,
      };
    }

    return null;
  }, [imageId, templateId, imageDetails]);

  const handleFailedToLoadData = useCallback(() => {
    notificationApi.showWarning(i18n.getString(_TL_('Failed to load. Please refresh the page and try again.')));
  }, [i18n, notificationApi]);

  useClarityTags([
    ClarityTags.LaunchedAdsStudio,
    ClarityTags.EditDisplayTemplateImage,
  ]);

  useEffect(() => {
    if (!displayAd) {
      displayTemplateEditPagePerfMarker.willFetchData();
      if (displayAdFromStorage) {
        setDisplayAd(displayAdFromStorage);
      } else {
        dataService.getImageById({ imageId }).then((image) => {
          if (!image.metadata || !image.metadata.template || image.metadata.template.templateId !== templateId) {
            navigation.gotoHomePage({ removeSearchQuery: [AdditionalQueries.TemplateId, AdditionalQueries.FolderId] });
          } else {
            setSourceType(image.sourceType);
            setDisplayAd({
              ...image.metadata,
              url: image.url,
            });
          }
        }).catch(() => {
          handleFailedToLoadData();
          logger.logTrace('failed to load');
        });
      }
    }
  }, [displayAdFromStorage, dataService, imageId, navigation, templateId, handleFailedToLoadData, logger, displayAd, displayTemplateEditPagePerfMarker]);

  useEffect(() => {
    if (!imageId || !templateId) {
      navigation.gotoHomePage({ removeSearchQuery: [AdditionalQueries.TemplateId, AdditionalQueries.FolderId] });
    }
  }, [imageId, templateId, navigation]);

  useEffect(() => {
    if (!templateConfig) {
      displayTemplateEditPagePerfMarker.willFetchData();
      displayTemplatesDataService
        .fetchDisplayAdsTemplateDefinition(templateId)
        .then(({ Data: templates }) => {
          const latestTemplate = _.max(
            templates.displayAdsTemplateDefinitions,
            templateDefinition => Number(templateDefinition.version)
          );
          setTemplateConfig(latestTemplate);
        })
        .catch(() => {
          handleFailedToLoadData();
          logger.logTrace('failed to load');
        });
    }
  }, [handleFailedToLoadData, logger, displayTemplatesDataService, templateId, templateConfig, displayTemplateEditPagePerfMarker]);

  useEffect(() => {
    if (!templateDetails && templateConfig && displayAd) {
      setTemplateDetails({
        displayAdsTemplateDetails: [
          {
            templateConfig,
            displayAd,
          },
        ],
      });
    }
  }, [displayAd, templateConfig, templateDetails]);

  return (
    <div className={classes.page}>
      {isPageLoading && (
        <Overlay>
          <AdsStudioSpinner
            className="page-load-spinner"
            useCustomSize
            customStyle={{ margin: 200 }}
          />
        </Overlay>
      )}
      {!isPageLoading && (
      <DisplayAdsEditor
        showSummaryBarControl
        i18n={i18n}
        brandkitApiState={brandkitApiState}
        brandKitDataService={brandKitDataService}
        hasFetchBrandKitData
        templateData={templateDetails}
        displayTemplatesDataService={displayTemplatesDataService}
        dataService={dataService}
        containerClassName={classes.editorContainer}
        serverContext={serverContext}
        preferenceService={preferenceService}
        perfMarker={displayTemplateEditPagePerfMarker}
        aid={aid}
        cid={cid}
        oDataConfig={oDataConfig}
        permissions={permissions}
        onSaveSuccess={() => {
          notify();
          navigation.gotoImagesPage({ removeSearchQuery: [AdditionalQueries.TemplateId, AdditionalQueries.FolderId] });
        }}
        navigation={navigation}
        instrumentation={instrumentation}
        scenarioName="EditDisplayTemplateImage"
        activityName="EditDisplayTemplateImage"
        sourceType={sourceType}
        setDataLoaded={setDataLoaded}
        folderId={folderId}
      />
      ) }
    </div>
  );
};

EditDisplayTemplatePage.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  displayTemplatesDataService: PropTypes.shape({
    fetchDisplayAdsTemplateDefinition: PropTypes.func,
  }),
  notificationApi: PropTypes.shape({
    showWarning: PropTypes.func,
  }),
  logger: PropTypes.shape({
    logTrace: PropTypes.func,
  }).isRequired,
  adsStudioUserPreferences: PropTypes.shape({}),
  reload: PropTypes.shape({}),
  navigation: PropTypes.shape({
    gotoHomePage: PropTypes.func,
    gotoImagesPage: PropTypes.func,
  }).isRequired,
  imageId: PropTypes.string.isRequired,
  brandKitDataService: PropTypes.shape({
    getBrandKits: PropTypes.func,
    createBrandKits: PropTypes.func,
    updateBrandKit: PropTypes.func,
    brandkitLoading: PropTypes.bool,
  }).isRequired,
  brandkitApiState: PropTypes.shape({
    brandkitResp: PropTypes.shape({}),
    brandkitError: PropTypes.shape({}),
    cache: PropTypes.shape({}),
    brandkitLoading: PropTypes.bool,
  }).isRequired,
  dataService: PropTypes.shape({
    getImageById: PropTypes.func,
  }).isRequired,
  permissions: PropTypes.shape({}).isRequired,
  aid: PropTypes.number.isRequired,
  cid: PropTypes.number.isRequired,
  oDataConfig: PropTypes.shape({}).isRequired,
  serverContext: PropTypes.shape({}).isRequired,
  preferenceService: PropTypes.shape({}).isRequired,
  pagePerfMarker: PropTypes.shape({
    willFetchData: PropTypes.func,
  }).isRequired,
  search: PropTypes.string.isRequired,
  instrumentation: PropTypes.shape({}).isRequired,
  setDataLoaded: PropTypes.func.isRequired,
};

EditDisplayTemplatePage.defaultProps = {
  displayTemplatesDataService: {},
  notificationApi: {},
  adsStudioUserPreferences: undefined,
  reload: null,
};

export const EditDisplayTemplatePageComponent = EditDisplayTemplatePage;
